import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const PRESETS = [
  {
    value: 'core',
    title: 'Editable fields',
    detail:
      'Names, Tally name, vendor code, status, commission and billing terms. '
      + 'This is the sheet Import reads back — edit it and upload it again.',
  },
  {
    value: 'full',
    title: 'All columns',
    detail:
      'Everything above plus billing address, warehouse and bank details. '
      + 'Those extra columns are read-only and are ignored on import.',
  },
];

/**
 * Preset chooser shown before a vendor export runs.
 *
 * Styling is inline and the tree is portalled to document.body, matching
 * ExportOrdersModal. A CSS-module version of this sat under the vendors page's own
 * stacking layers, so the overlay dimmed the screen while the dialog stayed hidden.
 */
function ExportVendorsModal({
  isOpen,
  onClose,
  selectedCount = 0,
  filterSummary = '',
  isExporting = false,
  onExport,
}) {
  const [preset, setPreset] = useState('core');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    containerRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Enter' && !isExporting) onExport(preset);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, onExport, preset, isExporting]);

  if (!isOpen) return null;

  const scopeLine =
    `Exporting ${selectedCount} vendor${selectedCount === 1 ? '' : 's'}`
    + (filterSummary ? ` matching ${filterSummary}.` : '.');

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={containerRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-vendors-title"
        style={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          zIndex: 1000000,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3
            id="export-vendors-title"
            style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600, color: '#111827' }}
          >
            Export Vendors
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              lineHeight: 1,
              cursor: 'pointer',
              color: '#6b7280',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '22px 24px' }}>
          <p
            style={{
              margin: '0 0 20px',
              padding: '11px 15px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              fontSize: '0.9rem',
              color: '#1e3a8a',
            }}
          >
            {scopeLine}
          </p>

          <p
            style={{
              margin: '0 0 11px',
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#6b7280',
            }}
          >
            Which columns?
          </p>

          {PRESETS.map((option) => {
            const active = preset === option.value;
            return (
              <label
                key={option.value}
                style={{
                  display: 'flex',
                  gap: '11px',
                  alignItems: 'flex-start',
                  padding: '14px 16px',
                  border: `1px solid ${active ? '#0d9488' : '#e5e7eb'}`,
                  backgroundColor: active ? '#f0fdfa' : '#ffffff',
                  borderRadius: '9px',
                  marginBottom: '11px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="vendor-export-preset"
                  value={option.value}
                  checked={active}
                  onChange={() => setPreset(option.value)}
                  style={{ marginTop: '3px', cursor: 'pointer', accentColor: '#0d9488' }}
                />
                <span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '0.93rem',
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    {option.title}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      marginTop: '4px',
                      fontSize: '0.8rem',
                      lineHeight: 1.5,
                      color: '#6b7280',
                    }}
                  >
                    {option.detail}
                  </span>
                </span>
              </label>
            );
          })}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              backgroundColor: '#ffffff',
              color: '#475569',
              fontSize: '0.88rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onExport(preset)}
            disabled={isExporting}
            style={{
              padding: '10px 22px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: isExporting ? '#cbd5e1' : '#0d9488',
              color: '#ffffff',
              fontSize: '0.88rem',
              fontWeight: 600,
              cursor: isExporting ? 'not-allowed' : 'pointer',
            }}
          >
            {isExporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default ExportVendorsModal;
